import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../components/contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import Breadcrumb from '../components/common/Breadcrumb';
import ROUTES from '../constants/routes';
import EntityTable, { renderActionButton } from '../components/common/EntityTable';
import EntityModal from '../components/common/EntityModal';
import FilterPanel from '../components/common/FilterPanel';
import Pagination from '../components/common/Pagination';
import ConfirmDialog from '../components/common/ConfirmDialog';
import HighlightedText from '../components/common/HighlightedText';
import HallazgoFotosPanel from '../components/hallazgos/HallazgoFotosPanel';
import usePaginatedEntity from '../hooks/usePaginatedEntity';
import { useToast } from '../components/contexts/ToastContext';
import apiService from '../services/apiService';
import { validateDate } from '../utils/sanitization';
import { renderEquipoCell } from '../utils/renderHelpers';
import { useExportExcel } from '../hooks/useExportExcel';
import { getDateKey } from '../utils/dateHelpers';
import '../styles/pages/hallazgos.css';

const ESTADO_VISUAL_CONFIG = {
  abierto:       { badge: 'badge-success',   label: 'Abierto' },
  ot_programado: { badge: 'badge-info',      label: 'OT Programada' },
  ot_en_progreso:{ badge: 'badge-warning',   label: 'OT En Progreso' },
  ot_completado: { badge: 'badge-success',   label: 'OT Completada' },
  cerrado:       { badge: 'badge-danger',    label: 'Cerrado' }
};

const PRIORIDAD_OPTIONS = [
  { value: 'baja',   label: 'Baja' },
  { value: 'media',  label: 'Media' },
  { value: 'alta',   label: 'Alta' },
  { value: 'critica', label: 'Crítica' }
];

/**
 * Página de gestión de hallazgos en equipos
 *
 * @param {Object} props
 * @param {Function} [props.fetchFn] - Override de función fetch (default: apiService.getHallazgos)
 * @param {string} [props.pageTitle='Hallazgos'] - Título para breadcrumb
 * @param {string} [props.exportName='Hallazgos'] - Nombre para exportación Excel
 * @param {boolean} [props.hideManagement=false] - Oculta acciones de gestión operativa (vincular OT, cerrar)
 * @returns {JSX.Element}
 */
function Hallazgos({ fetchFn: fetchFnProp, pageTitle = 'Hallazgos', exportName = 'Hallazgos', hideManagement = false }) {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();

  // Leer equipo_id desde URL params (para navegación desde Equipos)
  const searchParams = new URLSearchParams(window.location.search);
  const equipoIdFromUrl = searchParams.get('equipo_id') || '';

  // Hook CRUD principal
  const {
    data: hallazgos,
    rawData,
    loading,
    pagination,
    filters,
    modalOpen,
    editingItem: editingHallazgo,
    confirmDialog,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    handleCreate,
    handleEdit,
    handleDelete,
    confirmDelete,
    handleSave,
    closeModal,
    closeConfirm,
    refetch,
    sortField,
    sortOrder,
    handleSort
  } = usePaginatedEntity({
    entityName: 'hallazgo',
    entityNamePlural: 'hallazgos',
    fetchFn: fetchFnProp || apiService.getHallazgos,
    createFn: apiService.createHallazgo,
    updateFn: apiService.updateHallazgo,
    deleteFn: apiService.deleteHallazgo,
    defaultFilters: { search: '', equipo_id: equipoIdFromUrl },
    filterKeys: ['search', 'equipo_id'],
    messages: {
      createSuccess: 'Hallazgo creado',
      updateSuccess: 'Hallazgo actualizado',
      deleteSuccess: 'Hallazgo eliminado exitosamente'
    },
    onCreateSuccess: (hallazgo) => {
      if (hasPermission('hallazgos_fotos.read')) setSelectedHallazgo(hallazgo);
    }
  });

  // Catálogos
  const [equipos, setEquipos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [plantillas, setPlantillas] = useState([]);

  const exportToExcel = useExportExcel({
    data: rawData,
    mapFn: (h) => {
      const config = ESTADO_VISUAL_CONFIG[h.estado_visual] || ESTADO_VISUAL_CONFIG.abierto;
      return {
        'Código':          h.codigo,
        'Descripción':     h.descripcion || '',
        'Equipo':          h.equipo ? `${h.equipo.codigo} - ${h.equipo.marca} ${h.equipo.modelo}` : '',
        'Fecha Detección': h.fecha_deteccion || '',
        'Condiciona Op.':  h.condiciona_operacion ? 'Sí' : 'No',
        'Estado':          config.label,
        'OT Vinculada':    h.orden_trabajo?.codigo_ot || '',
        'Fotos':           h.fotos_count || 0,
        'Creado por':      h.created_by || ''
      };
    },
    sheetName: exportName,
    filePrefix: exportName.toLowerCase().replace(/\s+/g, '_') + '_',
    columnWidths: [
      { wch: 12 }, { wch: 40 }, { wch: 30 }, { wch: 16 },
      { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 8 }, { wch: 25 }
    ],
    entityLabel: 'hallazgos'
  });

  // Descripciones expandidas
  const [expandedDescs, setExpandedDescs] = useState(new Set());
  const toggleDesc = useCallback((id) => setExpandedDescs(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  }), []);

  // Sidebar de fotos
  const [selectedHallazgo, setSelectedHallazgo] = useState(null);

  // Modal Generar OT
  const [generarOtModal, setGenerarOtModal] = useState({ open: false, hallazgo: null });
  const [generarOtLoading, setGenerarOtLoading] = useState(false);
  const [otsEquipo, setOtsEquipo] = useState([]);

  // Modal Cerrar
  const [cerrarModal, setCerrarModal] = useState({ open: false, hallazgo: null });
  const [cerrarLoading, setCerrarLoading] = useState(false);

  const fetchCatalogos = useCallback(async () => {
    try {
      const [eqRes, usRes, plRes] = await Promise.all([
        apiService.getEquipos({ limit: 500 }),
        apiService.getUsuarios({ limit: 500 }),
        apiService.getPlantillasMantenimiento({ limit: 500 })
      ]);
      if (eqRes.success) setEquipos(eqRes.data || []);
      if (usRes.success) setUsuarios(usRes.data || []);
      if (plRes.success) setPlantillas(plRes.data || []);
    } catch (err) {
      console.error('Error fetching catalogos:', err);
    }
  }, []);

  const fetchOtsEquipo = useCallback(async (equipoId) => {
    if (!equipoId) { setOtsEquipo([]); return; }
    try {
      const response = await apiService.getOrdenesTrabajo({ equipo_id: equipoId, limit: 200 });
      if (response.success) {
        setOtsEquipo(
          (response.data || []).filter(ot => ot.estado === 'programado' || ot.estado === 'en_progreso')
        );
      }
    } catch (err) {
      setOtsEquipo([]);
    }
  }, []);

  useEffect(() => {
    fetchCatalogos();
  }, [fetchCatalogos]);

  // Handler Generar OT
  const handleGenerarOt = useCallback(async (formData) => {
    setGenerarOtLoading(true);
    try {
      let response;
      if (formData.modo === 'asociar') {
        response = await apiService.asociarOTAHallazgo(
          generarOtModal.hallazgo.id,
          { orden_trabajo_id: formData.orden_trabajo_id }
        );
      } else {
        const { modo, orden_trabajo_id, ...createData } = formData;
        response = await apiService.generarOTDesdeHallazgo(
          generarOtModal.hallazgo.id,
          createData
        );
      }
      if (response.success) {
        const accion = formData.modo === 'asociar' ? 'asociado a' : 'generada';
        showToast(`OT ${response.data.codigo_ot} ${accion}`, 'success');
        setGenerarOtModal({ open: false, hallazgo: null });
        refetch();
      }
    } catch (err) {
      showToast(err.message || 'Error al procesar OT', 'error');
    } finally {
      setGenerarOtLoading(false);
    }
  }, [generarOtModal.hallazgo, refetch, showToast]);

  // Handler Cerrar
  const handleCerrar = useCallback(async (formData) => {
    setCerrarLoading(true);
    try {
      const response = await apiService.cerrarHallazgo(cerrarModal.hallazgo.id, formData);
      if (response.success) {
        showToast('Hallazgo cerrado', 'success');
        setCerrarModal({ open: false, hallazgo: null });
        refetch();
      }
    } catch (err) {
      showToast(err.message || 'Error al cerrar hallazgo', 'error');
    } finally {
      setCerrarLoading(false);
    }
  }, [cerrarModal.hallazgo, refetch, showToast]);

  // Card renderer mobile
  const renderHallazgoMobileCard = useCallback((row, index, visibleActions) => {
    const config = ESTADO_VISUAL_CONFIG[row.estado_visual] || ESTADO_VISUAL_CONFIG.abierto;
    return (
      <div key={row.id} className="mobile-card hallazgo-card">

        <div className="mobile-card-header">
          <span className="mobile-card-title">
            <HighlightedText text={row.codigo} searchTerm={filters.search} />
          </span>
          <div className="hz-card-badges">
            <span className={`badge ${config.badge}`}>{config.label}</span>
            {row.condiciona_operacion ? (
              <span className="badge badge-danger">Condiciona Op.</span>
            ) : null}
          </div>
        </div>

        <div className="hz-specs">
          <div className="hz-spec">
            <span className="mobile-card-label">Equipo</span>
            <span className="mobile-card-value">
              {row.equipo?.codigo
                ? <HighlightedText text={row.equipo.codigo} searchTerm={filters.search} />
                : '-'}
            </span>
          </div>
          <div className="hz-spec">
            <span className="mobile-card-label">Detección</span>
            <span className="mobile-card-value">
              {row.fecha_deteccion
                ? new Date(row.fecha_deteccion).toLocaleDateString('es-CL')
                : '-'}
            </span>
          </div>
          <div className="hz-spec">
            <span className="mobile-card-label">Fotos</span>
            <span className="mobile-card-value">
              <i className="fas fa-camera"></i> {row.fotos_count || 0}
            </span>
          </div>
        </div>

        <div className="mobile-card-body">
          <div className="mobile-card-field hz-desc-field">
            <span className="mobile-card-label">
              <i className="fas fa-align-left"></i> Descripción
            </span>
            <span className="mobile-card-value">
              <p className={`hz-descripcion${expandedDescs.has(row.id) ? ' hz-descripcion--expanded' : ''}`}>
                <HighlightedText text={row.descripcion || '-'} searchTerm={filters.search} />
              </p>
            </span>
          </div>
          {row.descripcion && row.descripcion.length > 120 && (
            <button
              className="hz-desc-toggle"
              onClick={(e) => { e.stopPropagation(); toggleDesc(row.id); }}
            >
              {expandedDescs.has(row.id) ? 'Ver menos' : 'Ver más'}
            </button>
          )}
          {row.orden_trabajo && (
            <div className="mobile-card-field">
              <span className="mobile-card-label">
                <i className="fas fa-wrench"></i> OT
              </span>
              <span className="mobile-card-value">
                <HighlightedText text={row.orden_trabajo.codigo_ot} searchTerm={filters.search} />
              </span>
            </div>
          )}
        </div>

        {visibleActions?.length > 0 && (
          <div className="card-actions">
            {visibleActions.map(action =>
              renderActionButton(action, row, action.className?.includes('danger') ? 'btn-table-action' : 'btn-sm')
            )}
          </div>
        )}

      </div>
    );
  }, [filters.search, expandedDescs, toggleDesc]);

  // Columnas de tabla
  const columns = useMemo(() => [
    { key: 'codigo', label: 'Código', sortable: true, highlightable: true, title: true },
    {
      key: 'descripcion',
      label: 'Descripción',
      render: (row) => {
        const text = row.descripcion || '-';
        const isExpanded = expandedDescs.has(row.id);
        return (
          <>
            <p className={`hz-descripcion${isExpanded ? ' hz-descripcion--expanded' : ''}`}>
              <HighlightedText text={text} searchTerm={filters.search} />
            </p>
            {text.length > 120 && (
              <button
                className="hz-desc-toggle"
                onClick={(e) => { e.stopPropagation(); toggleDesc(row.id); }}
              >
                {isExpanded ? 'Ver menos' : 'Ver más'}
              </button>
            )}
          </>
        );
      }
    },
    {
      key: 'equipo',
      label: 'Equipo',
      render: (row, searchTerm) => renderEquipoCell(row.equipo, searchTerm)
    },
    {
      key: 'fecha_deteccion',
      label: 'Fecha Detección',
      sortable: true,
      render: (row) => row.fecha_deteccion
        ? new Date(row.fecha_deteccion).toLocaleDateString('es-CL')
        : '-'
    },
    {
      key: 'condiciona_operacion',
      label: 'Condiciona Op.',
      align: 'center',
      render: (row) => (
        <span className={`badge ${row.condiciona_operacion ? 'badge-danger' : 'badge-success'}`}>
          {row.condiciona_operacion ? 'Sí' : 'No'}
        </span>
      )
    },
    {
      key: 'estado_visual',
      label: 'Estado',
      sortable: true,
      align: 'center',
      render: (row, searchTerm) => {
        const config = ESTADO_VISUAL_CONFIG[row.estado_visual] || ESTADO_VISUAL_CONFIG.abierto;
        return (
          <span className={`badge ${config.badge}`}>
            <HighlightedText text={config.label} searchTerm={searchTerm} />
          </span>
        );
      }
    },
    {
      key: 'fotos_count',
      label: 'Fotos',
      render: (row) => (
        <span className="text-muted">
          <i className="fas fa-camera"></i> {row.fotos_count || 0}
        </span>
      )
    },
    {
      key: 'orden_trabajo',
      label: 'OT',
      render: (row) => row.orden_trabajo
        ? <HighlightedText text={row.orden_trabajo.codigo_ot} searchTerm={filters.search} />
        : <span className="text-muted">-</span>
    },
    { key: 'acciones', label: 'Acciones', align: 'center' }
  ], [filters.search, expandedDescs, toggleDesc]);

  // Campos modal CRUD
  const modalFields = useMemo(() => [
    {
      name: 'equipo_id',
      label: 'Equipo',
      type: 'select',
      required: true,
      options: equipos.map(e => ({ value: e.id, label: `${e.codigo} - ${e.marca} ${e.modelo}` }))
    },
    { name: 'descripcion', label: 'Descripción', type: 'textarea', required: true },
    {
      name: 'fecha_deteccion',
      label: 'Fecha de Detección',
      type: 'date',
      required: true,
      defaultValue: getDateKey(new Date()),
      validators: [(value) => validateDate(value, 'Fecha de detección')]
    },
    {
      name: 'condiciona_operacion',
      label: 'Condiciona Operación',
      type: 'checkbox'
    }
  ], [equipos]);

  // Campos modal Generar OT
  const generarOtFields = useMemo(() => [
    {
      name: 'modo',
      label: 'Acción',
      type: 'select',
      required: true,
      defaultValue: 'crear',
      options: [
        { value: 'crear', label: 'Crear nueva OT' },
        { value: 'asociar', label: 'Asociar a OT existente' }
      ]
    },
    {
      name: 'orden_trabajo_id',
      label: 'Orden de Trabajo',
      type: 'select',
      required: true,
      hidden: (formData) => formData.modo !== 'asociar',
      options: otsEquipo.map(ot => ({
        value: ot.id,
        label: `${ot.codigo_ot} — ${ot.estado} — ${(ot.descripcion || '').substring(0, 50)}${(ot.descripcion || '').length > 50 ? '...' : ''}`
      }))
    },
    {
      name: 'fecha_programada',
      label: 'Fecha Programada',
      type: 'date',
      required: true,
      hidden: (formData) => formData.modo !== 'crear',
      validators: [(value) => validateDate(value, 'Fecha programada')]
    },
    {
      name: 'prioridad',
      label: 'Prioridad',
      type: 'select',
      hidden: (formData) => formData.modo !== 'crear',
      options: PRIORIDAD_OPTIONS
    },
    {
      name: 'usuario_asignado_id',
      label: 'Técnico Asignado',
      type: 'select',
      hidden: (formData) => formData.modo !== 'crear',
      options: usuarios.map(u => ({ value: u.id, label: u.nombre || u.email }))
    },
    {
      name: 'plantilla_mantenimiento_id',
      label: 'Plantilla',
      type: 'select',
      hidden: (formData) => formData.modo !== 'crear',
      options: plantillas.map(p => ({ value: p.id, label: p.nombre }))
    },
    {
      name: 'descripcion_ot',
      label: 'Descripción OT',
      type: 'textarea',
      hidden: (formData) => formData.modo !== 'crear',
      placeholder: 'Dejar vacío para usar la descripción del hallazgo'
    }
  ], [usuarios, plantillas, otsEquipo]);

  // Campos modal Cerrar
  const cerrarFields = useMemo(() => [
    { name: 'motivo_cierre', label: 'Motivo de Cierre', type: 'textarea', required: true, placeholder: 'Indique el motivo por el cual se cierra sin generar OT...' }
  ], []);

  // Acciones de tabla
  const actions = useMemo(() => [
    {
      icon: 'fa-camera',
      className: 'btn-ghost-info',
      title: 'Ver fotos',
      label: 'Fotos',
      onClick: (row) => setSelectedHallazgo(row),
      show: () => hasPermission('hallazgos_fotos.read')
    },
    {
      icon: 'fa-wrench',
      className: 'btn-ghost-success',
      title: 'Vincular OT',
      label: 'OT',
      onClick: (row) => {
        setGenerarOtModal({ open: true, hallazgo: row });
        fetchOtsEquipo(row.equipo_id);
      },
      show: (row) => !hideManagement && hasPermission('hallazgos.generar_ot') && !row.orden_trabajo_id && row.estado === 'abierto'
    },
    {
      icon: 'fa-ban',
      className: 'btn-ghost-warning',
      title: 'Cerrar sin OT',
      onClick: (row) => setCerrarModal({ open: true, hallazgo: row }),
      show: (row) => !hideManagement && hasPermission('hallazgos.update') && !row.orden_trabajo_id && row.estado === 'abierto'
    },
    {
      icon: 'fa-edit',
      className: 'btn-ghost-primary',
      title: 'Editar',
      onClick: handleEdit,
      show: (row) => hasPermission('hallazgos.update') && row.estado === 'abierto' && !row.orden_trabajo_id
    },
    {
      icon: 'fa-trash',
      className: 'btn-ghost-danger',
      title: 'Eliminar',
      onClick: handleDelete,
      show: (row) => hasPermission('hallazgos.delete') && !row.orden_trabajo_id
    }
  ], [hideManagement, hasPermission, handleEdit, handleDelete, fetchOtsEquipo]);

  // Filtros
  const filterConfig = useMemo(() => [
    {
      name: 'search',
      type: 'text',
      placeholder: 'Buscar por código, descripción, equipo...',
      icon: 'fa-search'
    }
  ], []);

  return (
    <>
      <Navbar />
      <div className="main-content">
        <Breadcrumb items={[
          { label: 'Inicio', to: ROUTES.HOME },
          { label: pageTitle }
        ]} />

        <FilterPanel
          filters={filters}
          onFilterChange={handleFilterChange}
          filterConfig={filterConfig}
          actionConfig={[
            { label: 'Exportar Excel', icon: 'fa-file-excel', variant: 'secondary', onClick: exportToExcel },
            {
              label: 'Nuevo Hallazgo',
              icon: 'fa-plus',
              variant: 'primary',
              onClick: handleCreate,
              show: () => hasPermission('hallazgos.create')
            }
          ]}
        />

        <EntityTable
          columns={columns}
          data={hallazgos}
          loading={loading}
          searchTerm={filters.search}
          emptyMessage="No se encontraron hallazgos"
          onSort={handleSort}
          sortField={sortField}
          sortOrder={sortOrder}
          animateRows={true}
          actions={actions}
          mobileCardRenderer={renderHallazgoMobileCard}
        />

        <Pagination
          currentPage={pagination.page}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleLimitChange}
          loading={loading}
        />

        {/* Modal CRUD principal */}
        <EntityModal
          isOpen={modalOpen}
          onClose={closeModal}
          onSave={handleSave}
          title={editingHallazgo ? 'Editar Hallazgo' : 'Nuevo Hallazgo'}
          fields={modalFields}
          initialData={editingHallazgo}
        />

        {/* Modal Generar OT */}
        <EntityModal
          isOpen={generarOtModal.open}
          onClose={() => setGenerarOtModal({ open: false, hallazgo: null })}
          onSave={handleGenerarOt}
          title={`Vincular OT — ${generarOtModal.hallazgo?.codigo || ''}`}
          fields={generarOtFields}
          loading={generarOtLoading}
          confirmationMessage="El hallazgo quedará vinculado a la OT y no se podrá deshacer."
        />

        {/* Modal Cerrar */}
        <EntityModal
          isOpen={cerrarModal.open}
          onClose={() => setCerrarModal({ open: false, hallazgo: null })}
          onSave={handleCerrar}
          title={`Cerrar Hallazgo — ${cerrarModal.hallazgo?.codigo || ''}`}
          fields={cerrarFields}
          loading={cerrarLoading}
          confirmationMessage="Esta acción no se puede deshacer. El hallazgo se cerrará permanentemente."
        />

        {/* Confirm delete */}
        <ConfirmDialog
          isOpen={confirmDialog.open}
          onClose={closeConfirm}
          onConfirm={confirmDelete}
          title="Eliminar Hallazgo"
          message={`¿Está seguro de eliminar el hallazgo "${confirmDialog.item?.codigo}"?`}
          confirmText="Eliminar"
          variant="danger"
        />
      </div>

      {/* Sidebar de fotos */}
      {selectedHallazgo && (
        <HallazgoFotosPanel
          hallazgoId={selectedHallazgo.id}
          hallazgoCodigo={selectedHallazgo.codigo}
          onClose={() => setSelectedHallazgo(null)}
          canEdit={hasPermission('hallazgos_fotos.create') && selectedHallazgo.estado === 'abierto'}
          canDelete={hasPermission('hallazgos_fotos.delete') && selectedHallazgo.estado === 'abierto'}
        />
      )}
    </>
  );
}

export default Hallazgos;
